import React, { useEffect, useState, useRef } from 'react';
import Draggable from 'react-draggable';
import { MetadataUnion, PartUnion } from '../../models/GroupUnion';
import { GetSystemResponse, UnitDefinition, Part } from '../../models/Api';
import { useTranslation } from 'react-i18next';
import styles from '../PartCard/PartCard.module.scss';
import OutsideClickHandler from 'react-outside-click-handler';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DialogBox from '../DialogBox/DialogBox';
import SystemService from '../../services/SystemService';
import {
  getDataPrecisionForDivision,
  formatDecimals,
  findWorkflowStep,
  getNextActionButtonForPart,
  getNextActionForPart,
  getConvertedLabel,
  launchConfigureTool,
  convertUnit,
  needsConversion,
  isManifoldOptimized,
  getLinkedPartState,
  genericPartEditingConfirmed,
} from '../../utils/Utils';
import { WorkflowSteps } from '../../models/WorkflowSteps';
import { PartNextAction } from '../../models/PartNextAction';
import SizingToolService from '../../services/SizingToolService';
import SpinnerService from '../../services/SpinnerService';
import { isPartMountedOnManifold } from '../DashboardCanvas/ManifoldCanvasUtil';

function PartCard(props: {
  part: PartUnion;
  unitDefinitions: UnitDefinition[];
  system: GetSystemResponse;
  selectedPartId: string;
  handlePartDelete: () => void;
  handleLaunchSizingTool: (part: Part) => void;
  isSizingTabOpen: () => boolean;
  openSizingAlertDialog: () => void;
}) {
  const { t } = useTranslation();
  const nodeRef = useRef(null);
  const { part, unitDefinitions, system, selectedPartId, handlePartDelete, handleLaunchSizingTool, isSizingTabOpen, openSizingAlertDialog } = props;
  const [showPartCardMenuItems, setShowPartCardMenuItems] = useState(false);
  const [showTitleTooltip, setShowTitleTooltip] = useState(false);
  const [validateConfigureParts, setValidateConfigureParts] = useState({
    incompleteParts: '',
    showManifoldValidationAlert: false,
  });
  const [showPartDataTooltip, setShowPartDataTooltip] = useState(false);
  const isMetric = system.displayInMetricUnits as boolean;
  const partId = part.instance.dataItems?.find((dataItem) => dataItem.key === 'PartId')?.value;
  const otherPartNumbers = part.instance.dataItems?.find((dataItem) => dataItem.key === 'OtherPartNumbers')?.value;
  const decimalsToDisplay = getDataPrecisionForDivision(system.divisionId as number);
  const manifoldGroupId =
    part.category.name === 'PneumaticManifoldMasterPart'
      ? (part.instance.groupInstanceId as number)
      : (system?.groups?.find((group) => group.id === part.instance.groupInstanceId)?.parentGroupId as number);
  const [showOptimizedManifoldAlert, setShowOptimizedManifoldAlert] = useState(false);
  const [showConfirmPartResizing, setShowConfirmPartResizing] = useState(false);
  const [showConfirmGenericPartEditing, setShowConfirmGenericPartEditing] = useState(false);
  const [showCadInConfigurator, setShowCadInConfigurator] = useState(false);
  const partCardSilhouette = part?.category?.options && part?.category?.options?.find((option) => option.addOnDashboard)?.silhouetteUrl;
  const partInstance = part?.instance as Part;

  useEffect(() => {
    setShowTitleTooltip(false);
    setShowPartDataTooltip(false);
  }, []);

  function launchSizingTool() {
    if (!isSizingTabOpen()) {
      handleLaunchSizingTool(part.instance);
    } else {
      openSizingAlertDialog();
    }
  }

  async function partResizingConfirmed() {
    const workflowId = part.instance.workflowId;
    await SizingToolService.resetWorkFlow(workflowId as string);
    await SystemService.createPartSizingData(part.instance);
    if (system.partLinks?.some((partLink) => partLink.startId === part.instance.id)) {
      const linkedPartId = system.partLinks?.find((partlink) => partlink.startId === part.instance.id)?.endId;
      const linkedPart = system.parts?.find((segmentParts) => segmentParts.id === linkedPartId) as Part;
      await SizingToolService.resetWorkFlow(linkedPart.workflowId as string);
      await SystemService.createPartSizingData(linkedPart);
      const systemId = system?.id as string;
      await SystemService.refreshSystemById(systemId);
    } else {
      const overlappingGroup = props.system?.overlapGroupLinks?.find((overlapGroup) => overlapGroup.overlappingGroupId === part.instance.groupInstanceId);
      const linkedManifoldGroupId = system?.groups?.find((group) => group.id === overlappingGroup?.intersectedGroupId)?.parentGroupId as number;
      if (isManifoldOptimized(linkedManifoldGroupId)) {
        const reverted = await SystemService.revertManifold({ manifoldGroupId: linkedManifoldGroupId });
        if (!reverted) {
          return;
        }
      }
    }
    launchSizingTool();
  }

  const handleCadDrawingClick = () => {
    const isCadInConfigurator = part.instance.dataItems?.find((dataitem) => dataitem.key === 'IsCadInConfigurator')?.value;
    if (isCadInConfigurator === 'True') {
      setShowCadInConfigurator(true);
    } else {
      const url = part?.instance?.dataItems?.find((dataitem) => dataitem.key === 'CADUrl')?.value?.split('||')[0] as string;
      window.open(url, '_blank');
    }
  };

  const renderSummaryReportMenuItem = () => {
    const reportUrl = part.instance.dataItems?.find((dataitem) => dataitem.key === 'SummaryReportUrl')?.value;
    const reportData = part.instance.dataItems?.find((dataitem) => dataitem.key === 'SummaryReportData')?.value;
    const baseUrl = process.env.REACT_APP_LEGACY_BASE_HOST;
    const workflowId = part.instance.workflowId;
    if (reportUrl || (reportData && partId)) {
      const reportDataUrl = `${baseUrl}/Sizing/GetSummaryReport?WFID=${workflowId}&partId=${partId}`;
      const url = reportUrl ? reportUrl : reportDataUrl;
      return (
        <li>
          <a target="parkersummaryreport" href={url}>
            {t('SummaryReport')}
          </a>
        </li>
      );
    }
  };

  function createMetadata(partUnion: PartUnion) {
    const partMetadata: MetadataUnion[] = [];
    let Unit: UnitDefinition;
    partUnion.instance.metadataInstances?.map((partMDI) => {
      system?.partMetadataDefinitions?.map((partMDD) => {
        if (partMDD.id === partMDI.definitionId && partMDD.displayOnPartCard) {
          Unit = {};
          unitDefinitions.map((unit: UnitDefinition) => {
            if (
              partMDD.unitGroup !== 'None' &&
              unit?.unitGroup == partMDD.unitGroup &&
              unit?.unitSubgroup == partMDD.unitSubGroup &&
              unit?.unit == (partMDI.selectedUnit as string | number | undefined)
            ) {
              Unit = unit;
            }
          });
          Unit = Unit ?? {};
          const metadataUnion: MetadataUnion = {
            instance: {
              id: partMDI.id as number,
              name: partMDD.displayName as string,
              value: partMDI.value || '',
            },
            definition: partMDD,
            unitDefinition: Unit,
            needsConversion: false,
          };
          metadataUnion.needsConversion = needsConversion(metadataUnion);
          partMetadata.push(metadataUnion);
        }
      });
    });
    const metadataTable = partMetadata.map((metadata: MetadataUnion, idx: number) => {
      const unit = metadata.needsConversion ? getConvertedLabel(unitDefinitions, metadata, isMetric) : metadata.unitDefinition?.unitLabel;
      const value = formatDecimals(
        metadata.definition.dataType,
        metadata.needsConversion ? convertUnit(unitDefinitions, metadata, isMetric).toString() : metadata.instance.value,
        decimalsToDisplay,
      );
      return (
        <tr key={idx}>
          <td className={styles['part-metadata-name']}>{metadata.instance.name}</td>
          <td className={styles['part-metadata-value']}>
            {unit ? (
              <>
                <span>{value}</span>
                <span className={styles['part-metadata-unit']} title={unit}>
                  {unit}
                </span>
              </>
            ) : (
              value
            )}
          </td>
        </tr>
      );
    });
    return (
      <table className={styles['part-metadata-table']}>
        <tbody>{metadataTable}</tbody>
      </table>
    );
  }

  function partCardMenuItems() {
    const isPartOfManifold = part.category.name === 'PneumaticManifoldMasterPart' || isPartMountedOnManifold(part.instance);

    return (
      <>
        <ul className={showPartCardMenuItems ? styles['part-card-tile-menu-dropdown-open'] : styles['part-card-tile-menu-dropdown-close']}>
          {!isPartOfManifold && (
            <li
              onClick={() => {
                handlePartDelete();
              }}
            >
              <a>{t('RemovePart')}</a>
            </li>
          )}
          {part.instance.isParkerPart ? (
            <>
              {findWorkflowStep(part.instance) === WorkflowSteps.Add && (
                <li
                  onClick={() => {
                    handleLaunchSizingTool(part.instance);
                  }}
                >
                  <a>{t('HelpMeFindTheRightPart')}</a>
                </li>
              )}
              {findWorkflowStep(part.instance) >= WorkflowSteps.Size && (
                <li
                  onClick={async () => {
                    if (manifoldGroupId === null) {
                      const isSized = !!part.instance.workflowSteps?.find((step) => step.name === 'Configure')?.isCompleted;
                      if (part.instance.isParkerPart && isSized) {
                        setShowConfirmPartResizing(true);
                        return;
                      }
                    } else {
                      await SizingToolService.resetWorkFlow(part.instance.workflowId as string);
                      await SystemService.refreshSystemById(system.id as string);
                      if (isManifoldOptimized(manifoldGroupId)) {
                        setShowOptimizedManifoldAlert(true);
                        return;
                      }
                    }
                    launchSizingTool();
                  }}
                >
                  <a>{t('ViewEditSizing')}</a>
                </li>
              )}
            </>
          ) : (
            <>
              {part.instance.dataItems?.some((dataItem) => dataItem.key === 'SummaryReportData') === false && (
                <li
                  onClick={() => {
                    SizingToolService.setSizingToolState(part.instance, true, false, false);
                    SpinnerService.updateSpinnerStatus(0, false);
                  }}
                >
                  <a>{t('EnterPartInformation')}</a>
                </li>
              )}
              {part.instance.dataItems?.some((dataItem) => dataItem.key === 'SummaryReportData') && (
                <li
                  onClick={() => {
                    const linkedPartState = getLinkedPartState(part.instance.id as string);
                    if (linkedPartState.isSized) {
                      setShowConfirmGenericPartEditing(true);
                      return;
                    }
                    SizingToolService.setSizingToolState(part.instance, true, false, false);
                    SpinnerService.updateSpinnerStatus(0, false);
                  }}
                >
                  <a>{t('ViewEditPartInformation')}</a>
                </li>
              )}
            </>
          )}

          {part.instance.dataItems?.find((dataitem) => dataitem.key === 'ProductUrl')?.value && (
            <li>
              <a href={part.instance.dataItems?.find((dataitem) => dataitem.key === 'ProductUrl')?.value as string} target="_blank" rel="noreferrer">
                {t('ProductInfo')}
              </a>
            </li>
          )}

          {part.instance.dataItems?.find((dataitem) => dataitem.key === 'CADUrl')?.value && (
            <li>
              <a rel="noreferrer" onClick={() => handleCadDrawingClick()}>
                {t('CadDrawing')}
              </a>
            </li>
          )}
          {renderSummaryReportMenuItem()}
        </ul>
      </>
    );
  }
  return (
    <>
      <Draggable nodeRef={nodeRef}>
        <div ref={nodeRef} className={selectedPartId === part.instance.id ? styles['part-card-open'] : styles['part-card-close']}>
          <div className={styles['part-card-title-wrapper']}>
            <p>{part.instance.name}</p>

            {part.instance.isParkerPart && (
              <div className={styles[`part-workflow-step-${findWorkflowStep(part.instance)}`]}>
                <p className={styles['dashed-line']}></p>
                {part.instance.workflowSteps?.map((step) => (
                  <div key={step.name}>
                    <div className={styles['workflow-steps']}>
                      <span className={styles[`step-${step.name?.toLowerCase()}`]} />
                      <p className={styles[`step-title-${step.name?.toLowerCase()}`]}>{t(`${step.name}`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className={styles['tooltip-menuitems-div']}>
              <OutsideClickHandler
                onOutsideClick={() => {
                  setShowTitleTooltip(false);
                }}
              >
                <img src="images/question_sign.png" onClick={() => setShowTitleTooltip(!showTitleTooltip)} />
              </OutsideClickHandler>
              <OutsideClickHandler
                onOutsideClick={() => {
                  setShowPartCardMenuItems(false);
                }}
              >
                <FontAwesomeIcon icon="bars" className={styles['bars-icon']} onClick={() => setShowPartCardMenuItems(!showPartCardMenuItems)} />
              </OutsideClickHandler>
              {partCardMenuItems()}

              {showTitleTooltip && <div className={styles['title-tooltip']}>{t('ProgressBarInformingYou...')}</div>}
            </div>
          </div>
          <div className={styles['part-card-details']}>
            <div className={styles['part-name-number-div']}>
              <p>{t('CurrentComponentNamePartNumber')}</p>
              <div className={styles['part-name-number-field']}>
                {findWorkflowStep(part.instance) > WorkflowSteps.Add && (
                  <>
                    <div>{partId ? `${t('PartNumber')}: ${partId}` : part.instance.seriesVariableName}</div>
                    {otherPartNumbers && <div>{`${t('OtherPartNumbers')}: ${otherPartNumbers}`}</div>}
                    <br />
                    <div>{part.instance.shortDescription}</div>
                  </>
                )}
              </div>
            </div>
            {part.instance.recommendedAlternativePart && (
              <div className={styles['recommended-alternative-message']}>
                <FontAwesomeIcon className={styles['part-card-exclamation-icon']} icon="circle-exclamation" />
                {`${t('ThisPartIsNoLonger...')} ${part.instance.recommendedAlternativePart}`}
              </div>
            )}
            <div className={styles['part-photo-data-wrapper']}>
              <div className={styles['part-photo-div']}>
                <p>{t('Photo')}</p>
                <div className={styles['part-photo']}>
                  {part.instance.thumbnail ? (
                    <img
                      className={styles['part-tile-thumbnail']}
                      src={part.instance.thumbnail.substring(0, 2) === '//' ? `https:${part.instance.thumbnail}` : part.instance.thumbnail}
                    />
                  ) : (
                    <img
                      className={
                        styles[
                          part.instance.dataItems?.some((dataItem) => part.instance.isParkerPart === false && dataItem.key === 'SummaryReportData')
                            ? 'part-tile-silhouette-dark'
                            : 'part-tile-silhouette'
                        ]
                      }
                      src={`images/silhouette/${partCardSilhouette ? partCardSilhouette : 'parkerdefault.svg'}`}
                    />
                  )}
                </div>
              </div>
              <div className={styles['part-data-div']}>
                <div className={styles['part-data-tooltip-div']}>
                  <p>{t('PartData')}</p>
                  <OutsideClickHandler
                    onOutsideClick={() => {
                      setShowPartDataTooltip(false);
                    }}
                  >
                    <img src="images/question_sign.png" onClick={() => setShowPartDataTooltip(!showPartDataTooltip)} />
                  </OutsideClickHandler>
                  {showPartDataTooltip && <div className={styles['part-data-tooltip']}>{t('ThisShowsPartAttribute...')}</div>}
                </div>
                <div className={styles['part-data']}>{createMetadata(part)}</div>
              </div>
            </div>
            <div className={getNextActionForPart(part.instance) === PartNextAction.None ? styles['part-card-no-button-div'] : styles['part-card-button-div']}>
              {getNextActionForPart(part.instance) !== PartNextAction.None &&
                getNextActionButtonForPart(
                  part.instance,
                  isSizingTabOpen,
                  openSizingAlertDialog,
                  setValidateConfigureParts,
                  part,
                  setShowConfirmGenericPartEditing,
                )}
            </div>
          </div>
        </div>
      </Draggable>

      <DialogBox
        title={t('CannotSizeManifold')}
        message={t('PartsLinkedToValvesNotDone', { validateConfigureParts })}
        showModalState={validateConfigureParts.showManifoldValidationAlert}
        closeAction={() => {
          setValidateConfigureParts({
            incompleteParts: '',
            showManifoldValidationAlert: false,
          });
        }}
      />
      {showOptimizedManifoldAlert && (
        <OutsideClickHandler
          onOutsideClick={async (e: MouseEvent) => {
            const clickedButton = e.target as HTMLButtonElement;
            if (clickedButton.textContent === t('Yes')) {
              const reverted = await SystemService.revertManifold({ manifoldGroupId });
              if (reverted) {
                launchSizingTool();
              }
            } else {
              setShowOptimizedManifoldAlert(false);
            }
          }}
        >
          {showOptimizedManifoldAlert && (
            <DialogBox
              title={t('OptimizedManifoldAlertTitle')}
              message={t('OptimizedManifoldAlertMessage')}
              showModalState={showOptimizedManifoldAlert}
              confirmOptions={{
                confirmTranslationKey: 'Yes',
                confirmAction: () => {},
              }}
              closeAction={() => {
                setShowOptimizedManifoldAlert(false);
              }}
            />
          )}
        </OutsideClickHandler>
      )}
      {showConfirmPartResizing && (
        <OutsideClickHandler
          onOutsideClick={async (e: MouseEvent) => {
            const clickedButton = e.target as HTMLButtonElement;
            if (clickedButton.textContent === t('Yes')) {
              await partResizingConfirmed();
            }
          }}
        >
          <DialogBox
            title={t('ConfirmPartResizingChanges', { partInstance })}
            message={t('ConfirmPartResizingChangesMessage')}
            showModalState={showConfirmPartResizing}
            confirmOptions={{
              confirmTranslationKey: 'Yes',
              confirmAction: () => {
                partResizingConfirmed();
              },
            }}
            closeAction={() => {
              setShowConfirmPartResizing(false);
            }}
          />
        </OutsideClickHandler>
      )}
      {showConfirmGenericPartEditing && (
        <OutsideClickHandler
          onOutsideClick={async (e: MouseEvent) => {
            const clickedButton = e.target as HTMLButtonElement;
            if (clickedButton.textContent === t('Yes')) {
              await genericPartEditingConfirmed(partInstance);
            }
          }}
        >
          <DialogBox
            title={t('ConfirmPartResizingChanges', { partInstance })}
            message={t('ConfirmGenericPartEditingMessage')}
            showModalState={showConfirmGenericPartEditing}
            confirmOptions={{
              confirmTranslationKey: 'Yes',
              confirmAction: () => {
                genericPartEditingConfirmed(partInstance);
              },
            }}
            closeAction={() => {
              setShowConfirmGenericPartEditing(false);
            }}
          />
        </OutsideClickHandler>
      )}
      {showCadInConfigurator && (
        <OutsideClickHandler
          onOutsideClick={(e: MouseEvent) => {
            const clickedButton = e.target as HTMLButtonElement;
            if (clickedButton.textContent === t('OK')) {
              const iframeUrl = part.instance.dataItems?.find((cadUrl) => cadUrl.key === 'CADUrl')?.value as string;
              const tabType = 'CAD';
              launchConfigureTool(undefined, iframeUrl, `CAD - ${SizingToolService.getSizingToolTitle(system as GetSystemResponse, part.instance)}`, tabType);
            } else {
              setShowCadInConfigurator(false);
            }
          }}
        >
          {showCadInConfigurator && (
            <DialogBox
              title={t('ShowConfiguratorCAD')}
              message={t('ShowConfiguratorCADMessage')}
              showModalState={showCadInConfigurator}
              confirmOptions={{
                confirmTranslationKey: 'OK',
                confirmAction: () => {},
              }}
              closeAction={() => {
                setShowCadInConfigurator(false);
              }}
            />
          )}
        </OutsideClickHandler>
      )}
    </>
  );
}

export default PartCard;
